import {DotIcon} from '@primer/octicons-react'
import {BranchName, Link, NavList} from '@primer/react'
import {useLayoutEffect, useRef} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {
  buildPullRequestUserActionContext,
  type PullRequestUserActionStack,
} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import styles from './StackList.module.css'
import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import {StackMergeabilityEntryNavItem} from './StackMergeabilityEntryNavItem'
import {useLayoutQuery} from '@github-ui/pull-requests/page-data/loaders/use-layout-query'

function StackHeadingLink({number}: {number: number}) {
  const {repository} = useLayoutQuery().data
  // NavList gives direct anchor children primary-link styles instead of the muted heading color.
  return (
    <span>
      <Link
        inline
        muted
        className={styles.StackHeadingLink}
        href={`/${repository.ownerLogin}/${repository.name}/stack/${number}`}
      >
        Stack #{number}
      </Link>
    </span>
  )
}

export function StackList({
  baseRefName,
  currentPullRequestId,
  headingLevel,
  stackMetadata,
  stackEntries,
  autoMergeRangePullRequestCount,
}: {
  baseRefName?: string
  currentPullRequestId: string
  headingLevel: 'h3' | 'h4'
  stackMetadata?: PullRequestUserActionStack
  stackEntries: StackSectionStackEntry[]
  autoMergeRangePullRequestCount: number
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {pathname} = useLocation()
  const tabSuffix = getPullRequestTabSuffix(pathname)
  const currentEntryRef = useRef<HTMLAnchorElement>(null)

  // On mount, bring the current stack entry PR into view by scrolling the NavList (the scroll container) not
  // the page. A layout effect measures after layout so the scroll sticks, and the empty deps array will ensure that
  // later per-entry status changes or user initiated container scrolling will never re-scroll back to the current stack entry.
  useLayoutEffect(() => {
    const currentEntry = currentEntryRef.current
    if (!currentEntry) return

    const stackListNavContainer = currentEntry?.closest('nav')
    if (!stackListNavContainer) return

    const containerRect = stackListNavContainer.getBoundingClientRect()
    const currentEntryRect = currentEntry.getBoundingClientRect()
    const isFullyVisible = currentEntryRect.top >= containerRect.top && currentEntryRect.bottom <= containerRect.bottom
    if (isFullyVisible) return

    const currentScrollTop = stackListNavContainer.scrollTop
    const offsetWithinContainer = currentEntryRect.top - containerRect.top
    const centeringOffset = (containerRect.height - currentEntryRect.height) / 2
    stackListNavContainer.scrollTo({top: currentScrollTop + offsetWithinContainer - centeringOffset})
  }, [])

  const sendSwitchBranchEvent = (targetPullRequestId: number, targetPositionInStack: number, isCurrent: boolean) => {
    if (!stackMetadata || isCurrent) return

    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'merge_box',
        action: 'stack_switch_branch',
        data: {
          stack: stackMetadata,
          target_pull_request_id: targetPullRequestId,
          target_position_in_stack: targetPositionInStack,
        },
      }),
    )
  }

  return (
    <NavList className={styles.stackNavList} aria-label="Stacked pull requests list" disableItemGap>
      <NavList.Group hideDivider>
        {stackMetadata && (
          <NavList.GroupHeading as={headingLevel}>
            {isFeatureEnabled('pull_requests_stacks_view_ui') ? (
              <StackHeadingLink number={stackMetadata.number} />
            ) : (
              `Stack #${stackMetadata.number}`
            )}
          </NavList.GroupHeading>
        )}
        {stackEntries.map((e, idx) => {
          return (
            <StackMergeabilityEntryNavItem
              key={e.pull.number}
              entry={e}
              entryRef={e.position === 'CURRENT' ? currentEntryRef : undefined}
              tabSuffix={tabSuffix}
              currentPullRequestId={currentPullRequestId}
              autoMergeRangePullRequestCount={autoMergeRangePullRequestCount}
              onSwitchBranch={() => {
                sendSwitchBranchEvent(e.pull.number, idx + 1, e.position === 'CURRENT')
              }}
            />
          )
        })}
        {baseRefName && stackEntries.length > 0 && (
          <li className={styles.branchItem}>
            <span className={styles.branchItemContent}>
              <DotIcon className="color-fg-muted" />
              <BranchName as="span">{baseRefName}</BranchName>
            </span>
          </li>
        )}
      </NavList.Group>
    </NavList>
  )
}
